import React, {
  useCallback,
  useEffect,
  useState,
  createContext,
  useContext,
} from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  MarkerType,
  Edge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { ConnectDropTarget } from "react-dnd";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import CustomNode from "./CustomNode";
import { NodePropertiesModal } from "../../components/NodePropertiesModal/NodePropertiesModal";
import { EdgeNamingModal } from "../../components/EdgeNamingModal/EdgeNamingModal";
import { useDispatch } from "react-redux";
import { setSelectedtheme } from "../../services/state/Process-modular/processModular";
import { Process } from "@/services/state/processes/processes-interface";

// Define types for props in Flow component
interface FlowProps {
  nodes: any[];
  edges: any[];
  drop: ConnectDropTarget;
  onAdd: () => void;
  onConnect: (params: any) => void;
  onSaveToDB: () => Promise<void>;
  setRfInstance: (instance: any) => void;
  onNodesChange: (changes: any) => void;
  onEdgesChange: (changes: any) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
  onFileRestore: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNodeDragStop: (event: any, node: any) => void;
  onNodeClick: (event: React.MouseEvent, node: any) => void;
}

// Theme Context
interface ThemeContextType {
  theme: "light" | "dark";
  // toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // const [theme, setTheme] = useState<"light" | "dark">(
  //   () => (localStorage.getItem("theme") as "light" | "dark") || "light"
  // );

  // const toggleTheme = () => {
  //   setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  // };

  const theme = useSelector(
    (state: RootState) => state.processModeler.selectedTheme
  );

  useEffect(() => {
    localStorage.setItem("theme", theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme }}>{children}</ThemeContext.Provider>
  );
};

// Flow Component
const Flow: React.FC<FlowProps> = ({
  nodes,
  edges,
  drop,
  onConnect,
  setRfInstance,
  onNodesChange,
  onEdgesChange,
  onEdgeClick,
  onNodeDragStop,
  onNodeClick,
}) => {
  const theme = useSelector(
    (state: RootState) => state.processModeler.selectedTheme
  );

  const nodeTypes = {
    custom: CustomNode,
  };

  const flowContainerStyle = {
    height: "80vh",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    color: "#ffffff",
    borderRadius: "20px",
    backdropFilter: "blur(10px)",
  };

  return (
    <div style={flowContainerStyle} ref={drop}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={setRfInstance}
        nodeTypes={nodeTypes}
        onNodeDragStop={onNodeDragStop}
        onEdgeDoubleClick={onEdgeClick}
        onNodeDoubleClick={onNodeClick}
      >
        {/* uncomment this to show controls */}
        {/* <Controls
          style={{
            backgroundColor: theme === "light" ? "#ffffff" : "#1a1a1a",
            border: `1px solid ${theme === "light" ? "#cccccc" : "#333333"}`,
            color: theme === "light" ? "#000000" : "#ffffff",
          }}
        /> */}
      </ReactFlow>
    </div>
  );
};

// ProcessModeler Component
interface ProcessModelerProps {
  nodes: any[];
  edges: any[];
  drop: ConnectDropTarget;
  onAdd: () => void;
  onConnect: (params: any) => void;
  onSaveToDB: () => Promise<void>;
  setRfInstance: (instance: any) => void;
  onNodesChange: (changes: any) => void;
  onEdgesChange: (changes: any) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
  onFileRestore: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNodeDragStop: (event: any, node: any) => void;
  handleNodeIoDetails: (
    id: string | undefined,
    nodeInfo: {
      name: string;
      description: string;
      displayName: string;
    },
    ioDetails: any,
    gatewayDetails:
      | {
          expression: string;
          nextNodeUuid: string;
        }[]
      | undefined,
    nodeExecDetails:
      | {
          integrationUuid: string;
          inputs: string[];
          targetVariableUuid: string;
        }
      | undefined,
    subProcessDetails:
      | {
          subProcess?: string;
          expression?: string | null;
          isDynamic?: boolean;
          runAs: string;
          subProcessVariables: {
            inputVar: string | null;
            expression: string;
            isDynamic: boolean;
          }[];
          subProcessOutputs: string[];
        }
      | undefined
  ) => void;
  isEdgeLabelModalOpen: boolean;
  onEdgeLabelModalClose: () => void;
  modalCoordinates: {
    x: number;
    y: number;
  };
  selectedEdge: Edge | undefined;
  handleEdgeSave: (newLabel: string) => void;
}

export const ProcessModeler: React.FC<ProcessModelerProps> = ({
  nodes,
  edges,
  drop,
  onAdd,
  onConnect,
  onSaveToDB,
  setRfInstance,
  onNodesChange,
  onEdgesChange,
  onEdgeClick,
  onFileRestore,
  onNodeDragStop,
  handleNodeIoDetails,
  isEdgeLabelModalOpen,
  onEdgeLabelModalClose,
  modalCoordinates,
  selectedEdge,
  handleEdgeSave,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const handleNodeClick = (event: React.MouseEvent, node: any) => {
    setSelectedNodeId(node.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNodeId(null);
  };

  const selectedProcessModelJson = useSelector(
    (state: RootState) => state.processModeler.selectedProcessModelJson
  );

  const handleDeleteKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Delete") {
        const deletedNodeIds = nodes
          .filter((node) => node.selected)
          .map((node) => node.id);
        onNodesChange(deletedNodeIds.map((id) => ({ id, type: "remove" })));
      }
    },
    [nodes, onNodesChange]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleDeleteKeyPress);
    return () => {
      window.removeEventListener("keydown", handleDeleteKeyPress);
    };
  }, [handleDeleteKeyPress]);

  return (
    <ThemeProvider>
      <ReactFlowProvider>
        <Flow
          nodes={nodes}
          edges={edges}
          drop={drop}
          onAdd={onAdd}
          onConnect={onConnect}
          onSaveToDB={onSaveToDB}
          setRfInstance={setRfInstance}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgeClick={onEdgeClick}
          onFileRestore={onFileRestore}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={handleNodeClick}
        />
        {selectedNodeId && (
          <NodePropertiesModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            nodeId={selectedNodeId}
            handleNodeIoDetails={handleNodeIoDetails}
          />
        )}
        <EdgeNamingModal
          isOpen={isEdgeLabelModalOpen}
          onClose={onEdgeLabelModalClose}
          coordinates={modalCoordinates}
          initialName={String(selectedEdge?.label) || ""}
          onEdgeSave={handleEdgeSave}
        />
      </ReactFlowProvider>
    </ThemeProvider>
  );
};
